import React, { useEffect, useMemo, useReducer, useState } from 'react';
import BsModal from '../Common/BsModal';
import TextInput from '../Common/TextInput';
import CallOut from '../Common/CallOut';
import Notification from '../Common/Notification';
import VendorSelector from '../Transaction/VendorSelector';
import { useTag } from '../../DataStore/TagContext';
import ajaxPeriod from '../../util/remote/ajaxPeriod';

const INITIAL_FORM = {
  loading: false,
  amount: '',
  payee: '',
  description: '',
  reference: '',
  tag: '0',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET':
      return { ...state, [action.key]: action.value };
    case 'RESET':
      return { ...INITIAL_FORM };
    case 'SAVING':
      return { ...state, loading: true };
    case 'DONE':
      return { ...state, loading: false };
    default:
      return state;
  }
};

const toNumber = (v) => {
  const n = Number(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const SplitOpeningBalanceModal = ({ show, onClose, onSuccess, record }) => {
  const [info, setInfo] = useState(null);
  const { getTagListing, tagListing } = useTag();

  const [form, dispatch] = useReducer(reducer, INITIAL_FORM);

  // derive fixed account + type from record (no user selection)
  const fixedAccountType = record?.account?.account_type || '';
  const fixedAccountId = record?.account?.account_id || '';
  const fixedAccountLabel = record?.account
    ? `${record.account.account_number} ${record.account.account_name}`
    : '';

  const originalAmount = useMemo(() => toNumber(record?.amount ?? record?.debit ?? 0), [record]);

  // payee required when splitting a Liability opening balance
  const payeeRequired = useMemo(() => fixedAccountType === 'Liability', [fixedAccountType]);

  useEffect(() => {
    getTagListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // initialize/reset when closing OR when record changes / modal opens
  useEffect(() => {
    if (!show) {
      dispatch({ type: 'RESET' });
      setInfo(null);
      return;
    }

    // on open, prefill reasonable defaults
    dispatch({
      type: 'RESET',
    });
    dispatch({ type: 'SET', key: 'reference', value: record?.transaction_ref || 'B/F' });
    dispatch({ type: 'SET', key: 'description', value: record?.description || 'OPENING BALANCE' });
    dispatch({ type: 'SET', key: 'tag', value: record?.tag?.tag_id || '0' });
    dispatch({ type: 'SET', key: 'payee', value: record?.payee?.payee_id || '0' });
    setInfo(null);
  }, [show, record]);

  const tagOptions = useMemo(() => {
    const opts = [{ label: '- No project -', value: '0' }];
    if (Array.isArray(tagListing)) {
      tagListing.forEach((t) => opts.push({ label: t.tag_name, value: t.tag_id }));
    }
    return opts;
  }, [tagListing]);

  const remainingHint = useMemo(() => {
    const splitAmt = toNumber(form.amount);
    const remaining = Math.max(0, originalAmount - splitAmt);
    return { splitAmt, remaining };
  }, [form.amount, originalAmount]);

  const validate = () => {
    if (!record?.transaction_id) return 'Missing original record.';
    if (!fixedAccountId) return 'Missing account on the original record.';
    if (!fixedAccountType) return 'Missing account type on the original record.';

    const n = toNumber(form.amount);
    if (!form.amount || n <= 0) return 'Please enter a valid split amount.';
    if (n > originalAmount) return `Split amount cannot exceed ${originalAmount.toLocaleString()}.`;
    if (payeeRequired && !String(form.payee || '').trim()) return 'Please enter a payee for this liability split.';
    if (String(form.reference || '').trim().length === 0) return 'Please enter a reference.';
    return null;
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();

    const err = validate();
    if (err) {
      setInfo(<Notification message={err} type="danger" />);
      return;
    }

    dispatch({ type: 'SAVING' });

    const payload = {
      ledger_id: record?.transaction_id,
      amount: toNumber(form.amount),
      payee_id: String(form.payee || '').trim() || '0',
      description: String(form.description || '').trim() || 'OPENING BALANCE',
      reference: String(form.reference || '').trim() || '',
      tag: form.tag || '0'
    };

    // Prefer a dedicated endpoint if you have/implement it
    const server_response = await ajaxPeriod.splitOpeningBalance(payload);

    dispatch({ type: 'DONE' });

    if (server_response?.status === 'OK') {
      dispatch({ type: 'RESET' });
      setInfo(<Notification message={server_response.message || 'Split saved.'} type="success" />);
      onSuccess?.(server_response.details);
    } else {
      setInfo(<Notification message={server_response?.message || 'Failed to split opening balance.'} type="danger" />);
    }
  };

  return (
    <BsModal
      show={show}
      onClose={onClose}
      title="Split Opening Balance"
      size="modal-lg"
      footer={
        <>
          <button type="button" className="btn btn-modern-secondary" onClick={onClose} disabled={form.loading}>
            Close
          </button>

          <button type="button" className="btn btn-modern-primary" onClick={handleSave} disabled={form.loading}>
            {form.loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fa fa-check-circle mr-2" /> Create Split Record
              </>
            )}
          </button>
        </>
      }
    >
      <div style={{ padding: 8 }}>
        <CallOut
          title="Tip"
          description="Use this to break down a lump-sum opening balance into a more specific record. The split record keeps the same Account and Type as the original."
        />

        {/* Original summary */}
        <div className="alert alert-light border mt-2">
          <div className="d-flex flex-wrap justify-content-between">
            <div>
              <div className="tx-12 text-muted">Original Record</div>
              <div className="font-weight-bold">{fixedAccountLabel || '—'}</div>
              <div className="tx-12 text-muted">
                Type: <span className="text-dark">{fixedAccountType || '—'}</span>
                {'  '}•{'  '}
                Ref: <span className="text-dark">{record?.transaction_ref || '—'}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="tx-12 text-muted">Original Amount</div>
              <div className="font-weight-bold">{originalAmount.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="mt-3">{info}</div>

        <form className="form-horizontal" method="post" onSubmit={handleSave}>
          <div className="row">

            {/* Split amount with max hint */}
            <div className="col-md-6">
              <TextInput
                type="number"
                label={`Split Amount (max: ${originalAmount.toLocaleString()})`}
                placeholder="0.00"
                leftIcon="fa fa-scissors"
                value={form.amount}
                onChange={(e) => dispatch({ type: 'SET', key: 'amount', value: e.target.value })}
                required
                wrap
              />
              <small className="text-muted d-block" style={{ marginTop: -6 }}>
                Remaining after split: <b>{remainingHint.remaining.toLocaleString()}</b>
              </small>
            </div>

            {/* Payee (only required for Liability) */}
            <div className="col-md-6">
              <VendorSelector
                onChange={(item) => dispatch({ type: 'SET', key: 'payee', value: item?.value })}
                label={payeeRequired ? 'Payee (Required)' : 'Payee (Optional)'}
                placeholder={payeeRequired ? 'e.g. Supplier' : 'e.g. Supplier / Customer'}
                value={form.payee}
                wrap
                showCreate={false}
              />
              {payeeRequired && (
                <small className="text-muted d-block" style={{ marginTop: -6 }}>
                  Tip: For unpaid invoices / payables, include who the balance is owed to.
                </small>
              )}
            </div>

            {/* Reference */}
            <div className="col-md-6">
              <TextInput
                type="text"
                label="Reference"
                placeholder="Reference/Note"
                leftIcon="fa fa-hashtag"
                value={form.reference}
                onChange={(e) => dispatch({ type: 'SET', key: 'reference', value: e.target.value })}
                required
                wrap
              />
            </div>

            {/* Project */}
            <div className="col-md-6">
              <TextInput
                type="select"
                label="Project (Optional)"
                placeholder="Select project (if applicable)"
                leftIcon="fa fa-tag"
                options={tagOptions}
                onChange={(item) => dispatch({ type: 'SET', key: 'tag', value: item?.value })}
                leftComponent={<i className="fa fa-tag link" onClick={() => getTagListing(true)} />}
                loading={!tagListing}
                value={form.tag}
                wrap
              />
            </div>

            {/* Description */}
            <div className="col-12">
              <TextInput
                type="textarea"
                label="Description"
                placeholder="Short note about this split..."
                leftIcon="fa fa-align-left"
                value={form.description}
                onChange={(e) => dispatch({ type: 'SET', key: 'description', value: e.target.value })}
                wrap
              />
            </div>
          </div>
        </form>
      </div>
    </BsModal>
  );
};

export default SplitOpeningBalanceModal;
